import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../../tools/layout";
import { articlesData } from "../blog/articledata";
import { Comments } from "../../components/comments/discussionembed";
import { CommentsCount } from "../../components/comments/commentcount";
import Other from "../other/other";
import { supabase } from "../../tools/supabase.js";
import "../../styles/article-page.css";

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [likesCount, setLikesCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  const article = articlesData[id];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setLoading(true);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const localLiked = localStorage.getItem(`article-liked-${id}`) === "true";
    setLiked(localLiked);

    const fetchLikes = async () => {
      const { data, error } = await supabase
        .from("article_likes")
        .select("likes")
        .eq("article_id", id)
        .maybeSingle();

      if (error) {
        console.error("Erreur récupération likes :", error);
        return;
      }

      if (data) {
        setLikesCount(data.likes);
      } else {
        const { error: insertError } = await supabase
          .from("article_likes")
          .insert([{ article_id: id, likes: 0 }]);

        if (insertError) {
          console.error("Erreur création ligne likes :", insertError);
          return;
        }

        setLikesCount(0);
      }
    };

    fetchLikes();
  }, [id]);

  const articleKeys = Object.keys(articlesData);
  const currentIndex = articleKeys.indexOf(id);

  const previousId = currentIndex > 0 ? articleKeys[currentIndex - 1] : null;
  const nextId =
    currentIndex < articleKeys.length - 1 ? articleKeys[currentIndex + 1] : null;

  const previousArticle = previousId ? articlesData[previousId] : null;
  const nextArticle = nextId ? articlesData[nextId] : null;

  const handleNavigate = (route) => {
    setLoading(true);
    navigate(route);
  };

  const handleLike = async () => {
    if (!id || liked || likeLoading) return;

    setLikeLoading(true);

    const newCount = likesCount + 1;

    setLikesCount(newCount);
    setLiked(true);
    localStorage.setItem(`article-liked-${id}`, "true");

    const { error } = await supabase
      .from("article_likes")
      .update({ likes: newCount })
      .eq("article_id", id);

    if (error) {
      console.error("Erreur mise à jour likes :", error);
      setLikesCount((prev) => Math.max(prev - 1, 0));
      setLiked(false);
      localStorage.removeItem(`article-liked-${id}`);
    }

    setLikeLoading(false);
  };

  useEffect(() => {
    if (!article) {
      document.title = "Article Not Found";
      return;
    }

    document.title = article.title;
  }, [article]);

  if (!article) {
    return (
      <Layout>
        <div className="article-read">
          <div className="article not-found">
            <div className="not-found-content">
              <h1>404</h1>
              <h2>Article Not Found</h2>

              <p>
                Oops... the article you're looking for doesn't exist or may have been removed.
              </p>

              <Link to="/blog" className="back-to-articles">
                <svg className="back-arrow"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none">
                  <path d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg> Back to All Articles
              </Link>
            </div>
          </div>

          <Other />
        </div>
      </Layout>
    );
  }

  const articleUrl = `${window.location.origin}/blog/${id}`;

  return (
    <Layout>
      <div className="article-read">
        <div className="article article-wrapper">
          {loading && (
            <div className="article-loader-overlay">
              <div className="spinner"></div>
            </div>
          )}

          {!loading && (
            <>
              <img src={article.image} alt={article.title} />
              <h1>{article.title}</h1>

              <div className="article-card-date-comments">
                <p className="article-date">
                  <span>{article.date}</span>
                  {/*
                  <span>·</span>
                  <CommentsCount
                    article={{
                      id: String(id),
                      title: article.title,
                      url: articleUrl,
                    }}
                  />
                  <span>·</span>
                  <span className="article-like-meta" aria-label={`${likesCount} likes`}>
                    <span className="article-like-meta-count">{likesCount}</span>
                    <span className="article-like-meta-heart"> Likes</span>
                  </span>
                   */}
                </p>
              </div>

              {article.content}

              <div className="article-end-actions">
                <a href="#/blog" className="back-to-articles">
                  <span className="back-arrow">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>READ MORE</span>
                </a>
                <button
                  type="button"
                  className={`article-like-button ${liked ? "liked" : ""}`}
                  onClick={handleLike}
                  disabled={liked || likeLoading}
                  aria-label="Like this article"
                >
                  <span className="article-like-heart">
                    <svg
                      className="like-icon"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 20.5c-.3 0-.6-.1-.8-.3C9.5 18.7 4 14.2 4 9.8
         4 7.1 6.1 5 8.8 5c1.6 0 3.1.8 4.2 2.1C14.1 5.8 15.6 5 17.2 5
         19.9 5 22 7.1 22 9.8c0 4.4-5.5 8.9-7.2 10.4-.2.2-.5.3-.8.3z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span className="article-like-count">{likesCount}</span>
                </button>
              </div>

              <div className="article-navigation">
                {previousArticle && (
                  <button
                    type="button"
                    className="article-nav-card article-nav-prev"
                    onClick={() => handleNavigate(`/blog/${previousId}`)}
                  >
                    <span className="article-nav-arrow">←</span>
                    <span className="article-nav-title">
                      {previousArticle.title}
                    </span>
                  </button>
                )}

                {nextArticle && (
                  <button
                    type="button"
                    className="article-nav-card article-nav-next"
                    onClick={() => handleNavigate(`/blog/${nextId}`)}
                  >
                    <span className="article-nav-title">
                      {nextArticle.title}
                    </span>
                    <span className="article-nav-arrow">→</span>
                  </button>
                )}
              </div>

              <Comments
                article={{
                  id: String(id),
                  title: article.title,
                  url: articleUrl,
                }}
              />
            </>
          )}
        </div>
        <Other />
      </div>
    </Layout>
  );
}